import type { Column } from "./types/column";
import {
  isEventTraitColumn,
  isInlineTraitColumn,
  isTraitColumn,
} from "./types/column";
import {
  TraitType,
  type TraitConfig,
  type FormulaTraitConfig,
} from "./types/trait-definitions";
import {
  type TraitColumnReference,
  traitColumnReferences,
} from "./trait-column-references";
import { isColumnPreviewBlocked } from "../privacy-utils";

// These trait types don't return raw column values
export const TRAIT_TYPES_THAT_DONT_REQUIRE_REDACTION: Set<TraitType> = new Set([
  TraitType.Count,
  TraitType.Sum,
  TraitType.Average,
  TraitType.Predictive,
]);

type ColumnWithPrivacySettings = {
  name?: string;
  column?: Column;
  disable?: boolean | null;
  disable_preview?: boolean | null;
  privacy_level?: string | null;
};

type Trait = {
  type: TraitType | string;
  config: TraitConfig;
  relationship?: {
    to_model?: { columns: ColumnWithPrivacySettings[] };
  } | null;
};

type ParentModel = {
  filterable_audience_columns: ColumnWithPrivacySettings[];
  traits: Trait[];
};

/**
 * Returns true if any column that the trait aggregates over is preview-blocked.
 * Pass the columns of the model the trait measures FROM (i.e. TraitDefinition.modelId).
 * For formula traits, pass columns from all models the trait can reference.
 */
export function traitNeedsRedaction(
  trait: Trait,
  parentModel: ParentModel,
): boolean {
  if (TRAIT_TYPES_THAT_DONT_REQUIRE_REDACTION.has(trait.type as TraitType)) {
    return false;
  }

  const columnsReferencedInTrait = extractColumnsReferencedInTrait(
    trait.type as TraitType,
    trait.config,
  );

  if (columnsReferencedInTrait.length === 0) return false;

  const referenceableColumns = trait.relationship?.to_model?.columns ?? [];

  if (trait.type === TraitType.Formula) {
    // for formula traits, check all columns in the parent and related models
    referenceableColumns.push(
      ...parentModel.filterable_audience_columns,
      ...parentModel.traits.flatMap(
        (t) => t.relationship?.to_model?.columns ?? [],
      ),
    );
  }

  const previewBlockedColumns = new Set(
    referenceableColumns
      .filter((col) =>
        isColumnPreviewBlocked({
          disable: col.disable ?? false,
          disablePreview: col.disable_preview ?? false,
          privacyLevel: col.privacy_level ?? null,
        }),
      )
      .map((col) => col.name),
  );

  return columnsReferencedInTrait.some((columnName) =>
    previewBlockedColumns.has(columnName),
  );
}

export function extractColumnsReferencedInTrait(
  type: TraitType,
  config: TraitConfig,
): string[] {
  return traitColumnReferences(type, config).flatMap(referencedColumnNames);
}

export type NestedInlineTrait = {
  type: TraitType;
  config: TraitConfig;
  modelId: string | null;
};

/**
 * A trait that a formula references, paired with the `referencedColumns[].alias`
 * the formula projects it under. `materialized` traits carry a `traitDefinitionId`
 * (resolved from the DB); `inline` traits carry their config directly.
 */
export type FormulaReferencedTrait = { alias: string } & (
  | { kind: "materialized"; traitDefinitionId: string }
  | ({ kind: "inline" } & NestedInlineTrait)
);

/**
 * Walks a formula's `referencedColumns` and returns every column that resolves
 * to a trait, paired with its projected alias.
 *
 * Trait previews project each formula input as its own output column (keyed by
 * `referencedColumns[].alias`), so a referenced trait that is itself preview-
 * blocked must have THAT column redacted too — not only the formula's output.
 * Aliases are carried here (rather than in the redaction-decision helpers) so
 * callers can map a redacted reference back to the projected column.
 */
export function extractReferencedTraitsFromFormula(
  config: FormulaTraitConfig,
): FormulaReferencedTrait[] {
  const referenced: FormulaReferencedTrait[] = [];
  for (const { alias, column } of config.referencedColumns) {
    if (column.type !== "related" && column.type !== "transformed") continue;
    const inner = column.column;
    if (isTraitColumn(inner)) {
      referenced.push({
        alias,
        kind: "materialized",
        traitDefinitionId: inner.traitDefinitionId,
      });
    } else if (isEventTraitColumn(inner)) {
      referenced.push({
        alias,
        kind: "inline",
        type: inner.traitType,
        config: inner.traitConfig,
        modelId: inner.filteredEvent.eventModelId,
      });
    } else if (isInlineTraitColumn(inner)) {
      // We don't resolve relationshipId → modelId here; inline aggregated
      // traits fall back to checking against all blocked column names.
      referenced.push({
        alias,
        kind: "inline",
        type: inner.traitType,
        config: inner.traitConfig,
        modelId: null,
      });
    }
  }
  return referenced;
}

/**
 * Extracts a formula's raw column inputs, paired with the alias each is projected
 * under in a preview and the model each reads.
 *
 * Formula previews project every input as its own column (keyed by
 * `referencedColumns[].alias`, a `getColumnAlias` hash) so the UI can show the
 * values that contributed to the formula. A raw input that points at a preview-
 * blocked source column must have THAT projected column redacted too — otherwise
 * the blocked value leaks under its hash alias even though the named column and
 * the formula output are redacted. Covers bare raw columns and related-wrapped
 * raw columns (the only referenced shapes that surface a raw source value).
 */
export function extractReferencedRawColumnsFromFormula(
  config: FormulaTraitConfig,
): { alias: string; name: string; modelId: string }[] {
  const referenced: { alias: string; name: string; modelId: string }[] = [];
  for (const { alias, column } of config.referencedColumns) {
    if (column.type === "raw") {
      referenced.push({ alias, name: column.name, modelId: column.modelId });
    } else if (column.type === "related" && column.column.type === "raw") {
      referenced.push({
        alias,
        name: column.column.name,
        modelId: column.column.modelId,
      });
    }
  }
  return referenced;
}

/**
 * Extracts the trait definition IDs that a formula trait derives from.
 *
 * Formula `referencedColumns` can wrap a materialized trait (a `TraitColumn`,
 * which carries a `traitDefinitionId`) inside a related or transformed column.
 * These nested traits — e.g. a most_frequent/first/last/list trait — can
 * themselves be preview-blocked, so the formula must be redacted when they are.
 * The IDs are returned separately (rather than mixed into the column-name list)
 * because they are resolved and checked recursively, not matched against the
 * blocked-column set.
 */
export function extractTraitReferencesFromFormula(
  config: FormulaTraitConfig,
): string[] {
  return extractReferencedTraitsFromFormula(config)
    .filter(
      (r): r is Extract<FormulaReferencedTrait, { kind: "materialized" }> =>
        r.kind === "materialized",
    )
    .map((r) => r.traitDefinitionId);
}

/**
 * Extracts the inline (non-materialized) traits a formula derives from.
 *
 * Unlike {@link extractTraitReferencesFromFormula}, these traits carry their
 * config inline rather than a `traitDefinitionId`, so they can't be resolved
 * from the DB — they're checked for redaction directly by recursing on the
 * returned config. Covers `event_trait` columns and inline aggregated/formula
 * traits wrapped in a related or transformed column.
 */
export function extractInlineTraitsFromFormula(
  config: FormulaTraitConfig,
): NestedInlineTrait[] {
  return extractReferencedTraitsFromFormula(config)
    .filter(
      (r): r is Extract<FormulaReferencedTrait, { kind: "inline" }> =>
        r.kind === "inline",
    )
    .map(({ type, config, modelId }) => ({ type, config, modelId }));
}

// The raw column name a reference reads, when it reads one. Trait columns and
// static values surface no raw column.
function referencedColumnNames(reference: TraitColumnReference): string[] {
  if (typeof reference === "string") return [reference];
  switch (reference.type) {
    case "raw":
      return [reference.name];
    case "related":
      return reference.column.type === "raw" ? [reference.column.name] : [];
    case "transformed":
    case "static_value":
      return [];
    default:
      throw new Error(`Unknown reference: ${reference satisfies never}`);
  }
}
